import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import styles from './business-header.module.scss';

import {
  Alert,
  CircleImage,
  DefaultButton,
  WorkDaysDropdown,
  formatPhoneNumber,
  ModalWindow,
  RoundTriangle,
  UniversalImage,
} from 'shared/ui';
import { EditImageComponent } from 'features/index';

import { workingDayTimeHandler } from '../model/working-day-time-handler';
import { AppLinks } from 'app/router/consts/app-links.enum';
import { businessStore } from 'entities/business/model/business-card.store';
import { UserRoleEnum, userStore } from 'entities/index';
import { useAppMediaQuery } from 'shared/hooks';

import {
  LocationSvg,
  PhoneSvg,
  SettingsGearSvg,
  WatchSvg,
  WebSvg,
} from 'shared/svg';

interface BusinessHeaderProps {
  isSettings?: boolean;
  pointPage?: boolean;
}

export const BusinessHeader: React.FC<BusinessHeaderProps> = observer(
  ({ isSettings = true, pointPage = false }) => {
    const isMobileL = useAppMediaQuery('mobileL');
    const navigate = useNavigate();

    const [showEditLogoImage, setShowEditLogoImage] = useState(false);
    const [showEditHeaderImage, setShowEditHeaderImage] = useState(false);
    const [isOpenDropdown, setIsOpenDropdown] = useState(false);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);

    const headerData = pointPage
      ? businessStore.pointHeaderData
      : businessStore.businessHeaderData;

    const canEditSettings =
      userStore.role === UserRoleEnum.OWNER ||
      (userStore.role === UserRoleEnum.ADMINISTRATOR && pointPage);

    const settingsButtonHandler = () => {
      if (!headerData?.id) return;

      const pathTemplate = pointPage
        ? AppLinks.PointSettingsLink
        : AppLinks.BusinessSettingsLink;

      navigate(pathTemplate.replace(':id', headerData.id));
    };

    // Объединенная функция для обновления изображений
    const handleImageUpdate = async (
      url: string,
      type: 'logo' | 'headerImage'
    ) => {
      if (!headerData?.id) return;

      try {
        const isLogo = type === 'logo';
        const payloadBase = { id: headerData.id, currentTime: new Date() };

        if (pointPage) {
          isLogo
            ? businessStore.updatePointHeaderData({
                ...payloadBase,
                logoUrl: url,
              })
            : businessStore.updatePointHeaderData({
                ...payloadBase,
                headerImageUrl: url,
              });
        } else {
          isLogo
            ? businessStore.updateBusinessHeaderData({
                ...payloadBase,
                logoUrl: url,
              })
            : businessStore.updateBusinessHeaderData({
                ...payloadBase,
                headerImageUrl: url,
              });
        }
      } catch (error) {
        console.error(`Ошибка при обновлении изображения (${type}):`, error);
      } finally {
        type === 'logo'
          ? setShowEditLogoImage(false)
          : setShowEditHeaderImage(false);
      }
    };

    const handleCopy = async () => {
      if (!headerData?.id) return;

      try {
        const linkPath = AppLinks.CustomerCreateBookingLink.replace(
          ':pointId',
          headerData.id
        );
        const urlToCopy = `https://book-up.ru${linkPath}`;

        await navigator.clipboard.writeText(urlToCopy);
        setAlertMessage('Ссылка скопирована в буфер обмена!');
      } catch (err) {
        setAlertMessage('Не удалось скопировать ссылку!');
      }
    };

    const handleCloseAlert = React.useCallback(() => setAlertMessage(null), []);

    return (
      <>
        <div className={styles.business_header__component}>
          {showEditLogoImage && (
            <ModalWindow onClickOutside={() => setShowEditLogoImage(false)}>
              <EditImageComponent
                setUrl={(url) => handleImageUpdate(url, 'logo')}
              />
            </ModalWindow>
          )}

          {showEditHeaderImage && (
            <ModalWindow onClickOutside={() => setShowEditHeaderImage(false)}>
              <EditImageComponent
                setUrl={(url) => handleImageUpdate(url, 'headerImage')}
              />
            </ModalWindow>
          )}

          <div className={styles.component__block_info}>
            <div className={styles.block_info__title}>
              <CircleImage
                imgSize={isMobileL ? 50 : 60}
                imgSrc={headerData?.logoUrl}
                onClick={
                  !isSettings ? () => setShowEditLogoImage(true) : undefined
                }
              />
              <div className={styles.title_logo_text}>{headerData?.name}</div>

              {isMobileL && isSettings && canEditSettings && (
                <div
                  className={styles.title__settings_button}
                  onClick={settingsButtonHandler}
                >
                  <SettingsGearSvg width={34} />
                </div>
              )}
            </div>

            <div className={styles.block_info__description}>
              {headerData?.description}
            </div>

            <div className={styles.block_info__properties}>
              <div className={styles.property_container}>
                <div className={styles.property_icon}>
                  <WatchSvg />
                </div>
                <div className={styles.property_info}>
                  <div
                    className={styles.property_work_time}
                    onClick={() => setIsOpenDropdown((prev) => !prev)}
                  >
                    {workingDayTimeHandler(
                      headerData?.dayWorkTime,
                      headerData?.weekWorkTime
                    )}
                    <RoundTriangle isRotated={isOpenDropdown} />

                    <div
                      className={`${styles.work_time__dropdown} ${
                        isOpenDropdown ? styles.dropdown_open : ''
                      }`}
                    >
                      {headerData?.weekWorkTime && (
                        <WorkDaysDropdown
                          options={headerData.weekWorkTime}
                          isOpen={isOpenDropdown}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.property_container}>
                <div className={styles.property_icon}>
                  <LocationSvg />
                </div>
                <div className={styles.property_info}>
                  {headerData?.address}
                </div>
              </div>

              <div className={styles.property_container}>
                <div className={styles.property_icon}>
                  <PhoneSvg />
                </div>
                <div className={styles.property_info}>
                  {headerData?.contactPhone &&
                    formatPhoneNumber(headerData.contactPhone)}
                </div>
              </div>

              <div className={styles.property_container}>
                <div className={styles.property_icon}>
                  <WebSvg />
                </div>
                <div className={styles.property_info}>
                  {headerData?.siteUrl ? (
                    <a
                      href={headerData.siteUrl}
                      className={styles.property_url}
                    >
                      {headerData.siteUrl}
                    </a>
                  ) : (
                    'Адрес сайта не указан'
                  )}
                </div>
              </div>

              {pointPage && (
                <div className={styles.property_container}>
                  <div className={styles.property_info}>
                    <DefaultButton
                      onClick={handleCopy}
                      buttonText="Скопировать ссылку для записи"
                      className={styles.button_copy_link}
                      color="green"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles.component__block_image}>
            {isSettings ? (
              !isMobileL && (
                <>
                  <UniversalImage imgSrc={headerData?.headerImageUrl} />
                  {canEditSettings && (
                    <div
                      className={styles.block_image__settings_button}
                      onClick={settingsButtonHandler}
                    >
                      <SettingsGearSvg width={34} />
                    </div>
                  )}
                </>
              )
            ) : (
              <UniversalImage
                editIconSize={50}
                imgSrc={headerData?.headerImageUrl}
                onClick={() => setShowEditHeaderImage(true)}
              />
            )}
          </div>
        </div>
        {alertMessage && (
          <Alert
            type={alertMessage.includes('скопирована') ? 'success' : 'error'}
            message={alertMessage}
            onClose={handleCloseAlert}
          />
        )}
      </>
    );
  }
);
